import type { AnswerInput, ChatSnapshot, MessageView } from "@typedoc/contracts/chat";
import { applySnapshot } from "@typedoc/contracts/chat/reducer";
import { AppError } from "../app-error";
import { createLiveFilesPort } from "../files/live-port";
import { contractRequest, type ContractCall } from "../contract-request";
import type { Action } from "../processes/draft";
import type { ProcessState, Refusal } from "../processes/types";
import type { RegionalOptions } from "../regional-request";
import { chatApiClient, type ChatApiClient } from "../rpc/chat";
import { framesUntil, sleep } from "../stream-frames";
import { follow as followChat, type FollowDeps } from "./live-follow";
import {
  toChat,
  toSearchResult,
  toSummary,
  withQueued,
  withRun,
  withStarted,
  withArchived,
  withoutQueued,
  type Held,
} from "./live-map";
import type { Chat, ChatSummary, Mention, NewChat, Reply, SearchResult } from "./types";
import { parseTerms } from "./view";

/**
 * The chat service the screens talk to when `VITE_CHAT_API=live`: the Chats API, drawn as the
 * types the screens already read. It answers the same calls as the sample service (`./client`) and
 * adds the ones a real service has: following a chat's stream, and what only its owner can do.
 *
 * A call goes through the same transport as Connections: the workspace's regional API, a bearer
 * token that lives in memory only and is minted once for the scope, one retry on a 401, and every
 * failure as the app's own `AppError`. Work in flight stops when the person or the workspace
 * changes (the token cache aborts it), and when the page that asked leaves (its own signal).
 *
 * Not carried: brought-in chats (the API takes no chat in a message, so one goes without them),
 * and processes (the canvas stays on the sample service). A message's files are uploaded first,
 * through `files`, and sent by ID. What a message mentions with `@` goes as kind and ID alone: the
 * server reads the names itself and shows its own snapshot back.
 */

/** The API's page size for the list; a workspace with more is read page by page. */
const LIST_PAGE = 50;
/** Pages read for the list and for a chat's earlier messages; the screens cannot ask for more. */
const MAX_LIST_PAGES = 20;
const MAX_EARLIER_PAGES = 20;
/** The server ends a stream within 55 seconds; one open longer than this is dead, not quiet. */
const MAX_STREAM_MS = 65_000;
/** A connection to the stream is answered at once or not at all. */
const CONNECT_TIMEOUT_MS = 15_000;

export type LiveConfig = {
  userId: string;
  workspaceId: string;
  options: RegionalOptions;
  /** The sample's canvas, which has no API behind it yet. */
  processes: {
    process(id: string, signal?: AbortSignal): Promise<ProcessState | null>;
    act(processId: string, action: Action): Promise<ProcessState | Refusal>;
  };
  api?: ChatApiClient;
  /** A key that makes a repeat of one send harmless; a new one for each. */
  newKey?: () => string;
  maxStreamMs?: number;
};

/**
 * The request body's `mentions`: the kind and ID of each, and no key at all when there are none,
 * since the API takes the key as absent and not as an empty list.
 */
function mentionsBody(mentions: readonly Mention[] | undefined) {
  if (mentions === undefined || mentions.length === 0) return {};
  return { mentions: mentions.map(({ kind, id }) => ({ kind, id })) };
}

export function createLiveChatClient(config: LiveConfig) {
  const { userId, workspaceId, options } = config;
  const api = config.api ?? chatApiClient;
  const newKey = config.newKey ?? (() => crypto.randomUUID());
  const maxStreamMs = config.maxStreamMs ?? MAX_STREAM_MS;
  const scope = { userId, workspaceId };
  /** Ends everything this client has under way, for good. */
  const life = new AbortController();
  /** The chats opened or followed here, as the reducer holds them. */
  const holds = new Map<string, Held>();

  const regional = (signal?: AbortSignal): RegionalOptions => ({
    ...options,
    timeoutMs: options.timeoutMs ?? CONNECT_TIMEOUT_MS,
    signal: signal ? AbortSignal.any([life.signal, signal]) : life.signal,
  });

  const request = <T>(signal: AbortSignal | undefined, call: ContractCall<ChatApiClient, T>) =>
    contractRequest(api, scope, regional(signal), call);

  /** The held chat, unless a stream has taken it further than a snapshot taken before it. */
  function adopt(id: string, fetched: Held): Held {
    const current = holds.get(id);
    if (current && current.state.lastSeq > fetched.state.lastSeq) return current;
    holds.set(id, fetched);
    return fetched;
  }

  /** The newest page, its in-flight events folded in, and every page before it. */
  async function loadHeld(id: string, signal?: AbortSignal): Promise<Held> {
    const snapshot = await request(signal, (c, ws, call) =>
      c.open({ params: { workspaceId: ws, chatId: id } }, call),
    );
    let older: MessageView[] = [];
    let before = snapshot.messages[0]?.seq;
    let more = snapshot.hasEarlier;
    for (let page = 0; more && before !== undefined && page < MAX_EARLIER_PAGES; page++) {
      const cursor = before;
      const result = await request(signal, (c, ws, call) =>
        c.earlier({ params: { workspaceId: ws, chatId: id }, query: { before: cursor } }, call),
      );
      older = [...result.messages, ...older];
      before = result.messages[0]?.seq;
      more = result.hasEarlier;
    }
    return { state: applySnapshot(snapshot), older };
  }

  async function openFrames(
    id: string,
    after: number,
    signal: AbortSignal,
  ): Promise<AsyncIterable<unknown>> {
    const { iterator, scopeSignal } = await request(signal, async (c, ws, call, access) => {
      const frames: AsyncIterable<unknown> = await c.stream(
        { params: { workspaceId: ws, chatId: id }, query: { after } },
        call,
      );
      return { iterator: frames[Symbol.asyncIterator](), scopeSignal: access.signal };
    });
    const stopping = AbortSignal.any([life.signal, signal]);
    return framesUntil(iterator, stopping, scopeSignal, maxStreamMs);
  }

  /** Puts what a command left in the held chat, and shows it. */
  function shown(id: string, change: (held: Held) => Held): Chat | null {
    const current = holds.get(id);
    if (!current) return null;
    const next = change(current);
    holds.set(id, next);
    return toChat(next, userId);
  }

  /** The chat after a command that left it as the server now has it: read from there. */
  async function reread(id: string): Promise<Chat> {
    return toChat(adopt(id, await loadHeld(id)), userId);
  }

  /** File storage for this person in this workspace: the Files API, same transport. */
  const files = createLiveFilesPort({ userId, workspaceId, options });

  return {
    files,

    /** Every chat the person can see; archived ones only when asked for. */
    async list(signal?: AbortSignal, archived = false): Promise<ChatSummary[]> {
      const all: ChatSummary[] = [];
      let before: string | undefined;
      for (let page = 0; page < MAX_LIST_PAGES; page++) {
        const cursor = before;
        const result = await request(signal, (c, ws, call) =>
          c.list(
            {
              params: { workspaceId: ws },
              query: {
                scope: "all",
                archived: archived ? "include" : "hide",
                limit: LIST_PAGE,
                ...(cursor ? { before: cursor } : {}),
              },
            },
            call,
          ),
        );
        all.push(...result.chats.map((chat) => toSummary(chat, userId)));
        if (result.next === null) break;
        before = result.next;
      }
      return all;
    },

    /** The chats opened last, newest first, for the sidebar. */
    async recent(signal?: AbortSignal): Promise<ChatSummary[]> {
      const chats = await request(signal, (c, ws, call) =>
        c.recent({ params: { workspaceId: ws } }, call),
      );
      return chats.map((chat) => toSummary(chat, userId));
    },

    async search(query: string, signal?: AbortSignal, archived = false): Promise<SearchResult[]> {
      if (parseTerms(query).length === 0) return [];
      const found = await request(signal, (c, ws, call) =>
        c.search(
          {
            params: { workspaceId: ws },
            query: { q: query, archived: archived ? "include" : "hide" },
          },
          call,
        ),
      );
      return found.map((result) => toSearchResult(result, userId));
    },

    /** The whole chat, or null when there is none by that id for this person. */
    async open(id: string, signal?: AbortSignal): Promise<Chat | null> {
      try {
        return toChat(adopt(id, await loadHeld(id, signal)), userId);
      } catch (error) {
        if (error instanceof AppError && error.code === "NOT_FOUND") {
          holds.delete(id);
          return null;
        }
        throw error;
      }
    },

    process: (id: string, signal?: AbortSignal) => config.processes.process(id, signal),
    act: (processId: string, action: Action) => config.processes.act(processId, action),

    /** Starts a chat from a first message. Abacor answers it. */
    async create(input: NewChat): Promise<Chat> {
      const key = newKey();
      const snapshot = await request(undefined, (c, ws, call) =>
        c.create(
          {
            params: { workspaceId: ws },
            body: {
              requestId: key,
              text: input.text,
              fileIds: (input.files ?? []).map(({ id }) => id),
              ...mentionsBody(input.mentions),
              ...(input.processId === undefined ? {} : { processId: input.processId }),
            },
          },
          call,
        ),
      );
      const held: Held = { state: applySnapshot(snapshot), older: [] };
      holds.set(snapshot.chat.id, held);
      return toChat(held, userId);
    },

    /** Adds a reply to a chat. Abacor answers it, or it waits its turn behind the answer under way. */
    async reply({ chatId, text, files: sentFiles = [], mentions }: Reply): Promise<Chat> {
      const key = newKey();
      const fileIds = sentFiles.map(({ id }) => id);
      const sent = await request(undefined, (c, ws, call) =>
        c.send(
          {
            params: { workspaceId: ws, chatId },
            body: { requestId: key, text, fileIds, ...mentionsBody(mentions) },
          },
          call,
        ),
      );
      return (
        shown(chatId, (held) =>
          sent.kind === "started"
            ? withStarted(held, sent.message, sent.runId)
            : withQueued(held, sent.queued, sent.runId),
        ) ?? (await reread(chatId))
      );
    },

    /**
     * Follows a chat for as long as the page is on it: the chat again each time the stream changes
     * what it shows, and null once it is not there for this person any more (unshared or removed).
     */
    async *follow(chatId: string, signal: AbortSignal): AsyncGenerator<Chat | null, void> {
      const own = AbortSignal.any([life.signal, signal]);
      /** How many scopes the token cache had dropped when the attempt now under way began. */
      let discardedAtStart = options.cache.discarded();
      const deps: FollowDeps = {
        load: (cancel) => loadHeld(chatId, cancel),
        frames: (after, cancel) => {
          discardedAtStart = options.cache.discarded();
          return openFrames(chatId, after, cancel);
        },
        read: () => holds.get(chatId) ?? null,
        replace: (held) => {
          holds.set(chatId, held);
        },
        wait: sleep,
        now: Date.now,
        // Only a clear that came after the attempt began can have cancelled it; a token that named
        // another workspace leaves the cache as it was, and asking again would only ask for ever.
        retryable: () =>
          options.cache.available(scope) && options.cache.discarded() !== discardedAtStart,
      };
      for await (const held of followChat(deps, own)) {
        if (held === null) {
          holds.delete(chatId);
          yield null;
          return;
        }
        yield toChat(held, userId);
      }
    },

    /**
     * What the API says about a chat held here that the screens do not draw yet: the run to stop,
     * the requests to answer, what is queued, and whether this person may act or only read.
     */
    details(chatId: string): ChatSnapshot | null {
      return holds.get(chatId)?.state.snapshot ?? null;
    },

    /** Stops the run Abacor is working on. The run is named so a late click cannot stop the next. */
    async stop(chatId: string, runId: string): Promise<Chat | null> {
      const { run } = await request(undefined, (c, ws, call) =>
        c.abort({ params: { workspaceId: ws, chatId, runId } }, call),
      );
      return shown(chatId, (held) => withRun(held, run));
    },

    /** Starts a new run from where a failed or waiting one left off. */
    async continueRun(chatId: string): Promise<Chat | null> {
      const { run } = await request(undefined, (c, ws, call) =>
        c.continue({ params: { workspaceId: ws, chatId } }, call),
      );
      return shown(chatId, (held) => withRun(held, run));
    },

    /** Answers a "Needs you" request, in words or by one of the options it offers. */
    async answerRequest(
      chatId: string,
      requestId: string,
      answer: AnswerInput["answer"],
    ): Promise<Chat | null> {
      const key = newKey();
      const { run } = await request(undefined, (c, ws, call) =>
        c.answerRequest(
          {
            params: { workspaceId: ws, chatId, requestId },
            body: { answerKey: key, answer },
          },
          call,
        ),
      );
      return shown(chatId, (held) => withRun(held, run));
    },

    async cancelRequest(chatId: string, requestId: string): Promise<Chat | null> {
      const { run } = await request(undefined, (c, ws, call) =>
        c.cancelRequest({ params: { workspaceId: ws, chatId, requestId } }, call),
      );
      return shown(chatId, (held) => withRun(held, run));
    },

    /** Takes back a message that is waiting behind the run, before Abacor reads it. */
    async withdraw(chatId: string, inboxItemId: string): Promise<Chat | null> {
      await request(undefined, (c, ws, call) =>
        c.withdraw({ params: { workspaceId: ws, chatId, inboxItemId } }, call),
      );
      return shown(chatId, (held) => withoutQueued(held, inboxItemId));
    },

    /**
     * Puts the chat away or brings it back. Only its owner can. The chat is answered as it is now,
     * or null when it is not held here (the lists learn of it by reading again).
     */
    async setArchived(chatId: string, archived: boolean): Promise<Chat | null> {
      const summary = await request(undefined, (c, ws, call) =>
        c.setArchived({ params: { workspaceId: ws, chatId }, body: { archived } }, call),
      );
      return shown(chatId, (held) => withArchived(held, summary.archivedAt));
    },

    /** Removes the chat for good. Only its owner can. */
    async remove(chatId: string): Promise<void> {
      await request(undefined, (c, ws, call) =>
        c.remove({ params: { workspaceId: ws, chatId } }, call),
      );
      holds.delete(chatId);
    },

    /** Lets go of everything under way and everything held, for good: sign-out, or a new person. */
    dispose(): void {
      life.abort();
      holds.clear();
    },
  };
}

export type LiveChatClient = ReturnType<typeof createLiveChatClient>;
